
import { Component, Mixins } from "vue-property-decorator";
import { validationMixin } from "vuelidate";
import { minValue, required, requiredIf } from "vuelidate/lib/validators";
import { namespace } from "vuex-class";
import vselect3 from "vselect3";
import { debounce, isEqual } from "lodash";

import AlarmActionsMixin from "@/mixins/AlarmActionsMixin";
import { DeviceDetails, DeviceType, GetDeviceForGroupByTypeParams, GetDeviceParams } from "@/store/devices/types";
import { Response } from "@/store/responses/types";
import { extractPropertyFromXml } from "@/utils/string-utils";

const Devices = namespace("devices");
const Responses = namespace("responses");

interface CameraSettingsMeta {
	numberOfSeconds: number;
	presetNumber?: number;
	recordPreAlarmFootage: boolean;
	recordQuality: string;
}

const debouncePeriod: number = process.env.NODE_ENV === "test" ? 1 : 500;

@Component({
	components: {
		"v-select-3": vselect3
	},
	validations: {
		currentAlarmAction: { objectID: { required, minValue: minValue(1) } },
		settingsMeta: { numberOfSeconds: { required } },
		hasDataChanged: {
			required: requiredIf(function() {
				return (
					isEqual(this.currentAlarmAction.settingsMeta, this.originalSettingsMeta) &&
					this.currentAlarmAction.objectID === this.originalObjectId
				);
			})
		}
	}
})
export default class ActionSetupRecordCamera extends Mixins(AlarmActionsMixin, validationMixin) {
	@Devices.Action private fetchDeviceById: ({
		deviceId,
		params
	}: {
		deviceId: number;
		params: GetDeviceParams;
	}) => Promise<void>;

	@Devices.Action private fetchDevicesByType: (params: GetDeviceForGroupByTypeParams) => Promise<void>;
	@Devices.Mutation("resetState") private resetDevicesState: () => void;
	@Devices.State private device: DeviceDetails;
	@Devices.State private devices: DeviceDetails[];
	@Responses.State private currentAlarm: Response;

	private originalObjectId: number = null;
	private originalSettingsMeta: string = null;
	private numberOfSeconds: string = null;
	private selectedDevice: DeviceDetails = null;

	private settingsMeta: CameraSettingsMeta = {
		numberOfSeconds: null,
		presetNumber: undefined,
		recordPreAlarmFootage: false,
		recordQuality: "Default"
	};

	private get qualityOptions(): string[] {
		return ["Lowest", "Low", "Default", "High", "Highest"];
	}

	public async created(): Promise<void> {
		this.resetDevicesState();

		await this.fetchDevicesByType({
			groupId: this.currentAlarm.groupID,
			deviceTypeId: DeviceType.Camera,
			filter: null
		});

		if (this.isEditing) {
			await this.fetchDevice();
			this.setStateForEditing();
		}

		//API is expecting JSON for this field
		this.originalSettingsMeta = JSON.stringify(this.settingsMeta);
	}

	private setStateForEditing(): void {
		this.originalObjectId = this.currentAlarmAction.objectID;
		this.selectedDevice = { deviceID: this.device.deviceID, title: this.device.title } as DeviceDetails;

		this.settingsMeta.recordPreAlarmFootage =
			extractPropertyFromXml(this.currentAlarmAction.settingsMeta, "RecordPrealarm").toLowerCase() ===
			String(true);

		this.settingsMeta.presetNumber = parseInt(
			extractPropertyFromXml(this.currentAlarmAction.settingsMeta, "Preset")
		);

		this.numberOfSeconds = extractPropertyFromXml(this.currentAlarmAction.settingsMeta, "NumImages");
		this.settingsMeta.numberOfSeconds = parseInt(this.numberOfSeconds);
		this.settingsMeta.recordQuality = extractPropertyFromXml(this.currentAlarmAction.settingsMeta, "Quality");
	}

	private async handleDeviceSelected(): Promise<void> {
		this.currentAlarmAction.objectID = this.selectedDevice && this.selectedDevice.deviceID;

		//Need to check it is already defined, as if it is already undefined,
		//vuelidate bugs and will never pass validation
		if (this.settingsMeta.presetNumber) {
			this.settingsMeta.presetNumber = undefined;
		}

		if (this.currentAlarmAction.objectID) {
			await this.fetchDevice();
			return;
		}
	}

	private async fetchDevice(): Promise<void> {
		await this.fetchDeviceById({ deviceId: this.currentAlarmAction.objectID, params: null });
	}

	private handleSettingsMetaChanged(): void {
		this.settingsMeta.numberOfSeconds = this.numberOfSeconds ? parseInt(this.numberOfSeconds) : null;

		//API is expecting JSON for this field
		this.currentAlarmAction.settingsMeta = JSON.stringify(this.settingsMeta);
	}

	private get showPresets(): boolean {
		return Boolean(this.currentAlarmAction.objectID && this.device && this.device.presets);
	}

	private handleDeviceSearch(searchTerm: string, loading: (boolean) => void): void {
		loading(true);
		this.debounceSearch(searchTerm, loading);
	}

	private debounceSearch = debounce(async (searchTerm: string, loading: (boolean) => void): Promise<void> => {
		await this.searchDevices(searchTerm, loading);
	}, debouncePeriod);

	private async searchDevices(searchTerm: string, loading: (boolean) => void): Promise<void> {
		await this.fetchDevicesByType({
			groupId: this.currentAlarm.groupID,
			deviceTypeId: DeviceType.Camera,
			filter: searchTerm
		});

		loading(false);
	}
}
